import { yupResolver } from "@hookform/resolvers/yup";
import DOMPurify from "dompurify";
import isEmpty from "lodash/isEmpty";
import { FC, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { usePostPOTransaction } from "../../../../api/services/client/clientApis";
import {
  OperationType,
  POAdjustmentParams,
  POTransactionType,
} from "../../../../api/services/client/models";
import {
  FormController,
  MuiBox,
  MuiButtonPrimary,
  MuiGrid,
  MuiModal,
  MuiRadioGroup,
  MuiTypography,
  WarningConfirmationModal,
} from "../../../../components";
import { valueWithoutFormat } from "../../../../components/textFieldCurrency/CurrencyUtils";
import { TextFieldCurrency } from "../../../../components/textFieldCurrency/TextFieldCurrency";
import { PAGE_IDS } from "../../../../helpers/constants";
import { commonSiteCoreKeys } from "../../../../helpers/constants/siteCoreKeys";
import {
  getCurrencyNameAndLocaleById,
  getFormattedAmount,
  getParsedBEErrors,
  getParsedErrorMessage,
  isNotEmpty,
  replaceHashWithValue,
} from "../../../../helpers/utils";
import { useMediaQueries } from "../../../../hooks/useMediaQueries";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { useToast } from "../../../../hooks/useToast";
import { currencyListSelector } from "../../../../redux/slices/appState/appStateSelector";
import { toggleLoader } from "../../../../redux/slices/appState/appStateSlice";
import { POManageTransactionsFormKey } from "../../modelsAndSchemas/Models";
import { ManageTransactionValidationSchema } from "../../modelsAndSchemas/ValidationSchema";
import { purchaseOrdersListSiteCoreKeys } from "../../siteCore/SiteCoreKeys";
import { PurchaseOrderManageTransactionWrapper } from "./PurchaseOrderManageTransaction.style";
import PurchaseOrderTransactions from "./transactionsTable/PurchaseOrderTransactions";

interface PurchaseOrderManageTransactionProps {
  purchaseOrderId: string;
  purchaseOrderNumber: string;
  currencyTypeId: string;
  currentPOBalance: number;
  open: boolean;
  onClose: () => void;
  onPurchaseOrderUpdated?: () => void;
}

export const pageId = PAGE_IDS.PO_MANAGE_TRANSACTIONS;
const CURRENT_PO_AMOUNT = `${pageId}-current-po-amount`;
const CURRENT_PO_AMOUNT_VALUE = `${pageId}-current-po-amount-value`;
const TRANSACTION_TYPE = `${pageId}-transaction-type`;
const SUBMIT_BUTTON = `${pageId}-submit-button`;

const PurchaseOrderManageTransaction: FC<
  PurchaseOrderManageTransactionProps
> = ({
  purchaseOrderId,
  purchaseOrderNumber,
  currentPOBalance,
  currencyTypeId,
  open,
  onClose,
  onPurchaseOrderUpdated,
}) => {
  const { commonLabels, fetchLabel, fetchValidationMessage } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const gridSize = { lg: 4, md: 6, xs: 6, sm: 6 };
  const currencyList = useSelector(currencyListSelector);

  const [amountInfoMessage, setAmountInfoMessage] = useState<string>("");
  const [currentTotal, setCurrentTotal] = useState<number>(0);
  const [currentPOBalanceLocal, setCurrentPOBalanceLocal] =
    useState<number>(currentPOBalance);

  const [isCancelWarningVisible, setIsCancelWarningVisible] = useState(false);

  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);

  const {
    control,
    getValues,
    setValue,
    resetField,
    handleSubmit,
    formState,
    setError,
  } = useForm({
    mode: "all",
    defaultValues: {
      [POManageTransactionsFormKey.transactionAmount]: "",
      [POManageTransactionsFormKey.transactionType]: POTransactionType.TopUp,
    },
    resolver: yupResolver(
      ManageTransactionValidationSchema(fetchValidationMessage),
    ),
  });
  const { transactionType, transactionAmount } = getValues();

  const { dirtyFields } = formState;

  const dispatch = useDispatch();
  const { displayToast } = useToast();
  const { isMobile } = useMediaQueries();

  const {
    mutateAsync: addTransaction,
    data: addTransactionData,
    isSuccess: isAddTransactionSuccess,
    isError: isAddTransactionError,
    isPending: isAddTransactionPending,
    error: addTransactionError,
  } = usePostPOTransaction();

  useEffect(() => {
    setIsSubmitDisabled(isAddTransactionPending);
    dispatch(
      toggleLoader({
        isVisible: isAddTransactionPending,
        message: commonLabels.loadingPleaseWait,
      }),
    );
  }, [isAddTransactionPending]);

  useEffect(() => {
    if (isAddTransactionSuccess && addTransactionData) {
      const toastMessage =
        transactionType === POTransactionType.TopUp
          ? getLabel("amountAddedMessage")
          : getLabel("amountDeductedMessage");
      displayToast(toastMessage, "success");
      onPurchaseOrderUpdated?.();
      resetField(POManageTransactionsFormKey.transactionAmount);
      setCurrentPOBalanceLocal(parseFloat(currentTotal.toFixed(2)));
      if (isAddTransactionSuccess) setIsSubmitDisabled(false);
    }
  }, [isAddTransactionSuccess, addTransactionData]);

  useEffect(() => {
    if (isAddTransactionError && addTransactionError) {
      const error = getParsedBEErrors(addTransactionError);
      if (isNotEmpty(error)) {
        displayToast(getParsedErrorMessage(addTransactionError), "error");
      }
    }
  }, [isAddTransactionError, addTransactionError]);

  const handleOnClose = () => {
    if (isEmpty(dirtyFields)) {
      onClose();
    } else {
      setIsCancelWarningVisible(true);
    }
  };

  const handleTransactionTypeChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    resetField(POManageTransactionsFormKey.transactionAmount);
    setValue(
      POManageTransactionsFormKey.transactionType,
      Number(e.target.value),
      {
        shouldValidate: true,
      },
    );
  };

  const getLabel = (key: keyof typeof purchaseOrdersListSiteCoreKeys) => {
    return fetchLabel(
      purchaseOrdersListSiteCoreKeys.purchaseOrderTitle,
      purchaseOrdersListSiteCoreKeys[key],
    );
  };

  const currencyDetails = getCurrencyNameAndLocaleById(
    currencyList,
    currencyTypeId,
  );

  const formatCurrency = (amount: number): string => {
    const formattedAmount = `${currencyDetails.currencyName} ${getFormattedAmount(amount, currencyDetails.currencyLocale)}`;
    return formattedAmount;
  };

  const onFinalSubmitClick = () => {
    const { transactionAmount, transactionType } = getValues();

    const params: POAdjustmentParams = {
      purchaseOrderId: purchaseOrderId ?? "",
      operationId: transactionType as OperationType,
      adjustmentAmount: Number(valueWithoutFormat(transactionAmount)),
    };
    addTransaction(params);
  };

  const checkTotalGreaterThanBalance = (total: number) => {
    const { transactionAmount } = getValues();

    if (isNotEmpty(transactionAmount) && total < 0) {
      setTimeout(() => {
        setError(POManageTransactionsFormKey.transactionAmount, {
          type: "custom",
          message: getLabel("enterAnAmountLessThanYourCurrentPOBalance"),
        });
      }, 10);
      return true;
    }
    return false;
  };

  const processOrderSubmission = () => {
    if (
      transactionType === POTransactionType.Deduct &&
      checkTotalGreaterThanBalance(currentTotal)
    )
      return;
    handleSubmit(onFinalSubmitClick)();
  };

  const handleTransactionAmountChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { value: transactionAmount } = event.target;
    const amount = Number(valueWithoutFormat(transactionAmount));
    let total = currentPOBalanceLocal;

    if (transactionType === POTransactionType.TopUp) {
      total += amount;
      setAmountInfoMessage(
        replaceHashWithValue(getLabel("totalPoAmount"), formatCurrency(total)),
      );
    } else if (transactionType === POTransactionType.Deduct) {
      total -= amount;
      checkTotalGreaterThanBalance(total);
      setAmountInfoMessage(
        replaceHashWithValue(
          getLabel("balancePoAmountText"),
          formatCurrency(total),
        ),
      );
    }
    setCurrentTotal(total);
  };

  const radioGroupOptions = [
    {
      label: getLabel("topUpAmountRadioButton"),
      value: POTransactionType.TopUp.toString(),
    },
    {
      label: getLabel("deductAmountRadioButton"),
      value: POTransactionType.Deduct.toString(),
    },
  ];

  return (
    <MuiModal
      open={open}
      onClose={handleOnClose}
      title={replaceHashWithValue(
        getLabel("manageTransactionHeading"),
        purchaseOrderNumber,
      )}
      hasLargeContent
      primaryButtonTitle={commonLabels.ok}
      onPrimaryButtonClick={onClose}
      onTertiaryButtonClick={() => {}}
      className="purchase-order-manage-transaction"
    >
      <PurchaseOrderManageTransactionWrapper>
        <MuiBox className="current-po-amount">
          <MuiTypography variant="h3" data-testid={CURRENT_PO_AMOUNT}>
            {getLabel("currentPoAmount")}
          </MuiTypography>
          <MuiTypography variant="h3Bold" data-testid={CURRENT_PO_AMOUNT_VALUE}>
            {formatCurrency(currentPOBalanceLocal)}
          </MuiTypography>
        </MuiBox>
        <MuiGrid className="transaction-type">
          <MuiBox className="transaction-type-radio">
            <FormController
              control={control}
              name={POManageTransactionsFormKey.transactionType}
            >
              <MuiRadioGroup
                label={getLabel("selectType")}
                value={getValues(
                  POManageTransactionsFormKey.transactionType,
                ).toString()}
                onChange={(e) => handleTransactionTypeChange(e)}
                data-testid={TRANSACTION_TYPE}
                options={radioGroupOptions}
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiBox>
          <MuiBox className="transaction-amount-container">
            <MuiGrid size={gridSize} className="transaction-type-grid">
              <MuiBox className="transaction-amount">
                <FormController
                  control={control}
                  name={POManageTransactionsFormKey.transactionAmount}
                >
                  <TextFieldCurrency
                    id={`${pageId}-${POManageTransactionsFormKey.transactionAmount}`}
                    formId={POManageTransactionsFormKey.transactionAmount}
                    locale={currencyDetails.currencyLocale}
                    isCurrencyChanged={currencyTypeId}
                    isLabelInvisible
                    label={
                      transactionType === POTransactionType.TopUp
                        ? getLabel("topUpAmount")
                        : getLabel("deductAmount")
                    }
                    placeholder={commonLabels.inputPlaceHolder}
                    zeroValidationMessage={fetchValidationMessage(
                      commonSiteCoreKeys.greaterThanZeroFieldValidator,
                    )}
                    formatErrorMessage={fetchValidationMessage(
                      commonSiteCoreKeys.alphabetNotAllowedValidator,
                    )}
                    getFinalValues={(value, _valueNonFormatted) => {
                      setValue(
                        POManageTransactionsFormKey.transactionAmount,
                        value,
                      );
                    }}
                    requiredARIALabel={commonARIALabel.required}
                    onChange={handleTransactionAmountChange}
                  />
                </FormController>
                {transactionAmount && isMobile && (
                  <MuiTypography
                    className="balance-po-amount"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(amountInfoMessage),
                    }}
                  />
                )}
              </MuiBox>
              <MuiBox className="transaction-button">
                <MuiButtonPrimary
                  onClick={processOrderSubmission}
                  className="submit-button"
                  disabled={isSubmitDisabled}
                  data-testid={SUBMIT_BUTTON}
                >
                  {transactionType === POTransactionType.TopUp
                    ? getLabel("topUpButtonText")
                    : getLabel("deductButtonText")}
                </MuiButtonPrimary>
              </MuiBox>
            </MuiGrid>
            {!isMobile && transactionAmount ? (
              <MuiTypography
                className="balance-po-amount"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(amountInfoMessage),
                }}
              ></MuiTypography>
            ) : null}
          </MuiBox>
        </MuiGrid>
        <PurchaseOrderTransactions
          currencyTypeId={currencyTypeId}
          purchaseOrderId={purchaseOrderId}
          refreshTrigger={isAddTransactionSuccess}
        />
        {isCancelWarningVisible && (
          <WarningConfirmationModal
            show={isCancelWarningVisible}
            onClose={() => {
              setIsCancelWarningVisible(false);
            }}
            onPrimaryButtonClick={() => {
              setIsCancelWarningVisible(false);
              onClose();
            }}
            secondaryButtonTitle={commonLabels.no}
            primaryButtonTitle={commonLabels.yes}
            labelContent={commonLabels.cancelButtonActionText}
          />
        )}
      </PurchaseOrderManageTransactionWrapper>
    </MuiModal>
  );
};

export default PurchaseOrderManageTransaction;
